import { ResetPasswordDto } from '../../../../api-client';
import { ErrorResponse } from '../../../../common/types/errorRespose';
import React, { useContext } from 'react';
import { useResetOwnPasswordForm } from '../../hooks/useResetOwnPasswordForm';
import styled from 'styled-components';
import { Box, Button, Flexbox, TextField, Typography } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../common/components/ErrorBox/ErrorBox';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ProfileMessages } from '../../Profile.messages';
import { NotificationsContext } from '../../../../common/notifications/Notifications';
import { useAuth } from '@hiiretail/core';

type ResetOwnPasswordFormProps = {
  resetOwnPassword: (resetPasswordDto: ResetPasswordDto) => void;
  error: ErrorResponse | null;
  isResetting: boolean;
};

type ResetOwnPasswordFormComponent = React.FC<ResetOwnPasswordFormProps>;

const ResetOwnPasswordFormWrapper = styled(Flexbox)`
  box-shadow: 0 1px 4px rgb(0 0 0 / 8%), 0 0 2px rgb(0 0 0 / 3%);
  padding: ${({ theme }) => theme.spacing(4)}px;
`;

type ResetOwnPasswordFormData = {
  currentPassword: string;
  newPassword: string;
};

export const ResetOwnPasswordForm: ResetOwnPasswordFormComponent = ({
  error,
  isResetting,
  resetOwnPassword,
}) => {
  const intl = useIntl();
  const { control, handleSubmit, formState } = useResetOwnPasswordForm();
  const { currentUser, firebaseApp } = useAuth();
  const { showNotification } = useContext(NotificationsContext);

  const verifyCurrentPassword = (currentPassword: string) => {
    firebaseApp.auth().tenantId = firebaseApp.auth().currentUser?.tenantId || '';
    return firebaseApp.auth().signInWithEmailAndPassword(currentUser?.email || '', currentPassword);
  };

  const onSubmit = (data: ResetOwnPasswordFormData) => {
    verifyCurrentPassword(data.currentPassword)
      .then(() => {
        resetOwnPassword({ newPassword: data.newPassword });
      })
      .catch(() => {
        showNotification('Current password is incorrect. Please try again');
      });
  };

  return (
    <ResetOwnPasswordFormWrapper style={{ height: '100%', width: '100%' }}>
      <Flexbox item container alignItems="center" gutter={2}>
        <Flexbox item>
          <Typography>{intl.formatMessage(ProfileMessages.resetPassword)}</Typography>
        </Flexbox>
      </Flexbox>
      <Box mb={4} />
      <ErrorBox error={error} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flexbox container direction="column" gutter={4}>
          <Flexbox item>
            <Controller
              name="currentPassword"
              defaultValue=""
              control={control}
              render={({ field: { ref, value, onChange } }) => (
                <TextField
                  tabIndex={0}
                  fullWidth
                  id="hii-current-password"
                  autoComplete="current-hii-password"
                  label={intl.formatMessage(ProfileMessages.currentPassword)}
                  name="currentPassword"
                  disabled={isResetting}
                  type={'password'}
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  error={formState?.errors?.currentPassword?.message}
                />
              )}
            />
          </Flexbox>
          <Flexbox item>
            <Controller
              name="newPassword"
              defaultValue=""
              control={control}
              render={({ field: { ref, value, onChange } }) => (
                <TextField
                  id="hii-new-password"
                  autoComplete="new-hii-password"
                  fullWidth
                  label={intl.formatMessage(ProfileMessages.newPassword)}
                  name="newPassword"
                  disabled={isResetting}
                  type="password"
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  error={formState?.errors?.newPassword?.message}
                />
              )}
            />
          </Flexbox>
          <Flexbox item container justifyContent="flex-end">
            <Button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={
                (formState?.errors && !!Object.entries(formState?.errors).length) ||
                Object.keys(formState?.dirtyFields).length < 2 ||
                isResetting
              }
            >
              {isResetting ? 'Resetting ...' : 'Reset'}
            </Button>
          </Flexbox>
        </Flexbox>
      </form>
    </ResetOwnPasswordFormWrapper>
  );
};
